import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { AccommodationData, AccommodationCardProps } from './types';
import './AccommodationCard.css';

// ─── Icons ────────────────────────────────────────────────────
const HomeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

const BuildingIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
    <path d="M9 22v-4h6v4" />
    <path d="M8 6h.01M16 6h.01M8 10h.01M16 10h.01M8 14h.01M16 14h.01" />
  </svg>
);

const ApartmentIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8" />
    <path d="M4 10V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4" />
    <path d="M12 4v6" />
    <path d="M2 20h20" />
    <path d="M6 14v2" />
    <path d="M18 14v2" />
  </svg>
);

const HomeIconLarge = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

const BuildingIconLarge = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
    <path d="M9 22v-4h6v4" />
    <path d="M8 6h.01M16 6h.01M8 10h.01M16 10h.01M8 14h.01M16 14h.01" />
  </svg>
);

const ApartmentIconLarge = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8" />
    <path d="M4 10V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4" />
    <path d="M12 4v6" />
    <path d="M2 20h20" />
    <path d="M6 14v2" />
    <path d="M18 14v2" />
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// ─── Helpers ──────────────────────────────────────────────────
const formatPrice = (price: number, currency: string, lang: string): string => {
  const locale = lang === 'en' ? 'en-GB' : 'it-IT';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
};

const getCity = (location: string): string => location.split(',')[0].trim();

// Fallback formatter: SCREAMING_SNAKE_CASE → Title Case
const formatSnakeCase = (code: string): string => {
  return code
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Translate duration: "4 WEEK" → "4 weeks" / "4 settimane"
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const translateDuration = (duration: string, t: any): string => {
  const match = duration.match(/^(\d+)\s+(.+)$/);
  if (!match) return duration;

  const [, amount, unit] = match;
  // API returns singular (WEEK), pluralize key if amount != 1
  const unitKey = amount === '1' ? unit : `${unit}S`;
  const translated = t(`accommodationCard.durations.${unitKey}`, { defaultValue: formatSnakeCase(unit) });
  return `${amount} ${translated}`;
};

const getCategoryLabel = (category: string | undefined, t: (key: string) => string): string => {
  if (!category) return t('accommodationCard.categories.unknown');
  const key = `accommodationCard.categories.${category}`;
  const translated = t(key);
  return translated === key ? formatSnakeCase(category) : translated;
};

const getCategoryIcon = (category?: string) => {
  switch (category) {
    case 'HOMESTAY': return <HomeIcon />;
    case 'RESIDENCE': return <BuildingIcon />;
    case 'APARTMENT': return <ApartmentIcon />;
    default: return <HomeIcon />;
  }
};

const getCategoryIconLarge = (category?: string) => {
  switch (category) {
    case 'HOMESTAY': return <HomeIconLarge />;
    case 'RESIDENCE': return <BuildingIconLarge />;
    case 'APARTMENT': return <ApartmentIconLarge />;
    default: return <HomeIconLarge />;
  }
};

// Country flag mapping from location string
const getCountryFlag = (location: string): string => {
  const locationLower = location.toLowerCase();
  if (locationLower.includes('uk') || locationLower.includes('united kingdom') ||
      locationLower.includes('england') || locationLower.includes('london')) return '🇬🇧';
  if (locationLower.includes('ireland') || locationLower.includes('dublin')) return '🇮🇪';
  if (locationLower.includes('usa') || locationLower.includes('united states')) return '🇺🇸';
  if (locationLower.includes('canada')) return '🇨🇦';
  if (locationLower.includes('australia')) return '🇦🇺';
  if (locationLower.includes('malta')) return '🇲🇹';
  if (locationLower.includes('spain')) return '🇪🇸';
  return '';
};

const formatDetailValue = (value: string | null | undefined, t: (key: string) => string, prefix: string): string => {
  if (!value) return t(`${prefix}.unknown`);
  const key = `${prefix}.${value}`;
  const translated = t(key);
  return translated === key ? value.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase()) : translated;
};

// ─── Detail Modal ─────────────────────────────────────────────
interface AccommodationDetailModalProps {
  accommodation: AccommodationData;
  onClose: () => void;
}

const AccommodationDetailModal: React.FC<AccommodationDetailModalProps> = ({ accommodation, onClose }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  // Use priceEur if available, otherwise fallback to price (for mock data compatibility)
  const displayPrice = accommodation.priceEur ?? accommodation.price;
  const displayCurrency = accommodation.priceEur ? 'EUR' : accommodation.currency;
  const perWeek =
    accommodation.durationWeeks && accommodation.durationWeeks > 0
      ? formatPrice(Math.round(displayPrice / accommodation.durationWeeks), displayCurrency, lang)
      : null;
  const showOriginalCurrency = accommodation.priceEur && accommodation.currency !== 'EUR';

  return (
    <div className="acc-modal-backdrop" onClick={onClose}>
      <div className="acc-modal-card" onClick={(e) => e.stopPropagation()}>
        <button className="acc-modal-close" onClick={onClose} aria-label={t('common.close')}>
          <CloseIcon />
        </button>

        {accommodation.photoUrl && (
          <div className="acc-modal-photo" style={{ backgroundImage: `url(${accommodation.photoUrl})` }}>
            <span className={`acc-modal-category acc-modal-category--${accommodation.category?.toLowerCase()}`}>
              {getCategoryIcon(accommodation.category)}
              {getCategoryLabel(accommodation.category, t)}
            </span>
          </div>
        )}

        <div className="acc-modal-body">
          <h2 className="acc-modal-name">{accommodation.name}</h2>
          <p className="acc-modal-school">{accommodation.schoolName}</p>

          <div className="acc-modal-details">
            <div className="acc-modal-detail-row">
              <span className="acc-modal-detail-label">{t('accommodationCard.modal.location')}</span>
              <span className="acc-modal-detail-value">{accommodation.location}</span>
            </div>
            <div className="acc-modal-detail-row">
              <span className="acc-modal-detail-label">{t('accommodationCard.modal.duration')}</span>
              <span className="acc-modal-detail-value">{translateDuration(accommodation.duration, t)}</span>
            </div>
            {accommodation.startDate && (
              <div className="acc-modal-detail-row">
                <span className="acc-modal-detail-label">{t('accommodationCard.modal.checkIn')}</span>
                <span className="acc-modal-detail-value">{accommodation.startDate}</span>
              </div>
            )}
            {accommodation.roomType && (
              <div className="acc-modal-detail-row">
                <span className="acc-modal-detail-label">{t('accommodationCard.modal.room')}</span>
                <span className="acc-modal-detail-value">{formatDetailValue(accommodation.roomType, t, 'accommodationCard.roomTypes')}</span>
              </div>
            )}
            {accommodation.bathroomType && (
              <div className="acc-modal-detail-row">
                <span className="acc-modal-detail-label">{t('accommodationCard.modal.bathroom')}</span>
                <span className="acc-modal-detail-value">{formatDetailValue(accommodation.bathroomType, t, 'accommodationCard.bathroomTypes')}</span>
              </div>
            )}
            {accommodation.mealType && (
              <div className="acc-modal-detail-row">
                <span className="acc-modal-detail-label">{t('accommodationCard.modal.meals')}</span>
                <span className="acc-modal-detail-value">{formatDetailValue(accommodation.mealType, t, 'accommodationCard.mealTypes')}</span>
              </div>
            )}
          </div>

          <div className="acc-modal-price-section">
            <div className="acc-modal-price-main">
              <span className="acc-modal-price">{formatPrice(displayPrice, displayCurrency, lang)}</span>
            </div>
            {perWeek && <span className="acc-modal-price-weekly">{perWeek} / {t('accommodationCard.perWeek')}</span>}
            {showOriginalCurrency && (
              <span className="acc-modal-price-original">
                (~{formatPrice(accommodation.price, accommodation.currency, lang)})
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Card Item ────────────────────────────────────────────────
interface AccommodationCardItemProps {
  accommodation: AccommodationData;
  index: number;
  onSelect: () => void;
}

const AccommodationCardItem: React.FC<AccommodationCardItemProps> = ({ accommodation, index, onSelect }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [logoError, setLogoError] = useState(false);

  // Use priceEur if available, otherwise fallback to price (for mock data compatibility)
  const displayPrice = accommodation.priceEur ?? accommodation.price;
  const displayCurrency = accommodation.priceEur ? 'EUR' : accommodation.currency;
  const flag = getCountryFlag(accommodation.location);
  const perWeek = accommodation.durationWeeks && accommodation.durationWeeks > 0
    ? formatPrice(Math.round(displayPrice / accommodation.durationWeeks), displayCurrency, lang)
    : null;
  const showOriginalCurrency = accommodation.priceEur && accommodation.currency !== 'EUR';

  // Build specs array
  const specs: string[] = [];
  if (accommodation.roomType) {
    specs.push(formatDetailValue(accommodation.roomType, t, 'accommodationCard.roomTypes'));
  }
  if (accommodation.bathroomType) {
    specs.push(formatDetailValue(accommodation.bathroomType, t, 'accommodationCard.bathroomTypes'));
  }
  if (accommodation.mealType) {
    specs.push(formatDetailValue(accommodation.mealType, t, 'accommodationCard.mealTypes'));
  }

  return (
    <article
      className={`acc-card acc-card--${accommodation.category?.toLowerCase() || 'default'}`}
      style={{ animationDelay: `${index * 0.05}s` }}
      onClick={onSelect}
    >
      {/* Logo on left */}
      <div className={`acc-card-logo acc-card-logo--${accommodation.category?.toLowerCase() || 'default'}`}>
        {accommodation.photoUrl && !logoError ? (
          <img
            src={accommodation.photoUrl}
            alt={accommodation.name}
            onError={() => setLogoError(true)}
          />
        ) : (
          <div className={`acc-card-logo-placeholder acc-card-logo-placeholder--${accommodation.category?.toLowerCase() || 'default'}`}>
            {getCategoryIconLarge(accommodation.category)}
          </div>
        )}
      </div>

      {/* Main content */}
      <div className="acc-card-main">
        {/* Header */}
        <div className="acc-card-header">
          <span className={`acc-card-category acc-card-category--${accommodation.category?.toLowerCase() || 'default'}`}>
            {getCategoryIcon(accommodation.category)}
            <span>{getCategoryLabel(accommodation.category, t)}</span>
          </span>
        </div>

        {/* Body */}
        <div className="acc-card-body">
          <h3 className="acc-card-name">{accommodation.name}</h3>
          <span className="acc-card-school">{accommodation.schoolName}</span>
          <span className="acc-card-meta">
            {flag && <span className="acc-card-flag">{flag}</span>}
            {getCity(accommodation.location)} · {translateDuration(accommodation.duration, t)}
          </span>

          {/* Specs preview */}
          {specs.length > 0 && (
            <div className="acc-card-specs">
              {specs.slice(0, 3).map((spec, i) => (
                <span key={i} className="acc-card-spec">{spec}</span>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="acc-card-footer">
          <div className="acc-card-prices">
            <span className="acc-card-price">{formatPrice(displayPrice, displayCurrency, lang)}</span>
          </div>
          {perWeek && (
            <span className="acc-card-price-weekly">{perWeek}/{t('accommodationCard.perWeek')}</span>
          )}
          {showOriginalCurrency && (
            <span className="acc-card-price-original">
              (~{formatPrice(accommodation.price, accommodation.currency, lang)})
            </span>
          )}
        </div>
      </div>
    </article>
  );
};

// ─── Main Component ───────────────────────────────────────────
export const AccommodationCard: React.FC<AccommodationCardProps> = ({ data, portalContainer }) => {
  const { t } = useTranslation();
  const accommodations = Array.isArray(data) ? data : [data];
  const [selectedAccommodation, setSelectedAccommodation] = useState<AccommodationData | null>(null);

  return (
    <div className="card-section-wrapper">
      <div className="card-section-header">
        <div className="card-section-title-row">
          <h2 className="card-section-title">{t('accommodationCard.sectionTitle')}</h2>
          <span className="card-section-count">{accommodations.length}</span>
        </div>
      </div>
      <div className="card-grid-2col">
        {accommodations.map((accommodation, index) => (
          <AccommodationCardItem
            key={accommodation.accommodationId || index}
            accommodation={accommodation}
            index={index}
            onSelect={() => setSelectedAccommodation(accommodation)}
          />
        ))}
      </div>

      {selectedAccommodation && (
        portalContainer
          ? createPortal(
              <AccommodationDetailModal
                accommodation={selectedAccommodation}
                onClose={() => setSelectedAccommodation(null)}
              />,
              portalContainer
            )
          : <AccommodationDetailModal
              accommodation={selectedAccommodation}
              onClose={() => setSelectedAccommodation(null)}
            />
      )}
    </div>
  );
};
